import { Pipe, PipeTransform, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';

/**
 * Markdown pipe for rendering user-defined Markdown content as sanitized HTML.
 *
 * Usage:
 *   <span [innerHTML]="content | markdown"></span>
 *
 * For internal link routing (to prevent page reload):
 *   <span internalLinkRouting [innerHTML]="content | markdown"></span>
 *
 * Features:
 * - Marks internal links with class="internal-link" for Angular router navigation
 */
@Pipe({
  name: 'markdown',
  pure: true,
  standalone: true
})
export class MarkdownPipe implements PipeTransform {
  private sanitizer = inject(DomSanitizer);

  transform(markdown: string): SafeHtml {
    const renderer = new marked.Renderer();
    renderer.link = (token: any) => {
      // Disallow data: URLs, allow everything else
      if (token.href && token.href.toLowerCase().startsWith('data:')) {
        // Return just the text for data: URLs (remove the link)
        return token?.text;
      }
      // Mark internal links (same origin or relative paths) for Angular routing
      const isInternal = this.isInternalLink(token.href);
      const classAttr = isInternal ? ' class="internal-link"' : '';
      return `<a href="${this.escapeHtml(token?.href)}"${classAttr}>${token?.text}</a>`;
    };
    marked.use({
      gfm: true,
      breaks: false,
      renderer,
    });
    let html = marked.parse(markdown);
    // Remove unsafe HTML tags (script, style, iframe, etc.)
    // Allow safe formatting tags: paragraphs, headings, lists, formatting, links, code blocks
    html = html.replace(/<(?!\/?(p|h[1-6]|strong|em|code|pre|ul|ol|li|br|a|blockquote|hr|img|table|thead|tbody|tr|th|td)\b)[^>]+>/gi, '');
    html = html.replace(/on\w+="[^"]*"/gi, '').replace(/javascript:/gi, '');
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private isInternalLink(href: string): boolean {
    if (!href) return false;
    // Relative paths and anchors are internal
    if (href.startsWith('/') || href.startsWith('#')) return true;
    // Check if link is to current domain
    try {
      const linkUrl = new URL(href, window.location.href);
      return linkUrl.origin === window.location.origin;
    } catch {
      return false;
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
